/**
 * Phase 266d — Embed Performance tile (Academy Owner dashboard).
 *
 * Surfaces the data already collected by the Phase 266c Top Moments
 * pixel beacon — turns the silent telemetry into a visible self-service
 * growth proof:
 *
 *   • Impressions (how many times the embed widget actually entered
 *     viewport on partner pages — NOT just script loads)
 *   • CTR (impression → "View wall →" cta_click conversion)
 *   • Top 5 referrer hosts (which partner pages drove the most views)
 *
 * Honest behaviour:
 *   • Self-hides when there is zero telemetry (no fake numbers).
 *   • Self-hides on auth errors (non-owner roles silently skip the tile).
 *   • Uses the REAL endpoint `/api/academy/top-moments-pixel/report` —
 *     no invented backend route.
 *   • Links to the existing widget embed instructions section in Settings
 *     so owners who don't see traffic can grab their embed snippet.
 */
import { useEffect, useState } from 'react';
import { Activity, Eye, MousePointerClick, Globe, Heart, Check, Code2 } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import ReferrerThankYouDialog from './ReferrerThankYouDialog';
import EmbedTopMomentsBlock from './EmbedTopMomentsBlock';

function formatPct(ctr) {
  if (ctr === null || ctr === undefined) return '—';
  return `${(Number(ctr) * 100).toFixed(1)}%`;
}

function formatInt(n) {
  if (n === null || n === undefined) return '0';
  return Number(n).toLocaleString();
}

export default function EmbedPerformanceTile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [thankedHosts, setThankedHosts] = useState(new Set());
  const [dialogHost, setDialogHost] = useState(null);
  const [snippetOpen, setSnippetOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Fetch report + active cooldowns in parallel so the "Thanked"
        // pill is rendered correctly on first paint (no flicker).
        const [reportRes, historyRes] = await Promise.allSettled([
          api.get('/academy/top-moments-pixel/report', { params: { days: 30 } }),
          api.get('/academy/top-moments-pixel/thank-you-history'),
        ]);
        if (!mounted) return;
        if (reportRes.status === 'fulfilled') {
          setData(reportRes.value?.data || null);
        }
        if (historyRes.status === 'fulfilled') {
          const cd = historyRes.value?.data?.active_cooldowns || [];
          setThankedHosts(new Set(cd.map((r) => r.target_host)));
        }
      } catch (_e) {
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSent = (host) => {
    // Optimistically mark this host as thanked so the button flips to
    // the "Thanked" pill until the 7-day cooldown elapses.
    setThankedHosts((prev) => {
      const next = new Set(prev);
      next.add(host);
      return next;
    });
  };

  // Self-hide while loading and for non-owner roles (data stays null on
  // 401/403). When impressions === 0 we render a compact zero-state that
  // surfaces the "Get embed code" CTA so new academies can bootstrap from
  // the dashboard without hunting in /settings.
  if (!loaded) return null;
  if (!data) return null;
  const impressions = Number(data.impressions || 0);
  const ctaClicks = Number(data.cta_clicks || 0);
  const ctr = data.ctr;
  const topHosts = Array.isArray(data.top_referrer_hosts) ? data.top_referrer_hosts : [];
  const isZeroState = impressions <= 0;

  return (
    <Card data-testid="embed-performance-tile" className="border-emerald-200 dark:border-emerald-900/60 bg-gradient-to-br from-emerald-50/60 via-teal-50/40 to-cyan-50/40 dark:from-emerald-950/20 dark:via-teal-950/15 dark:to-cyan-950/15">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0 gap-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Activity className="h-4 w-4 text-emerald-600" />
          Embed performance · last 30 days
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSnippetOpen(true)}
          className="h-7 text-[11px] font-bold border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
          data-testid="embed-perf-get-snippet-btn"
        >
          <Code2 className="h-3 w-3 mr-1.5" />
          Get embed code
        </Button>
      </CardHeader>
      <CardContent>
        {isZeroState ? (
          <div
            className="rounded-md bg-white/60 dark:bg-slate-950/30 border border-emerald-100 dark:border-emerald-900/40 px-3 py-3 text-[12px] text-slate-600 dark:text-slate-300"
            data-testid="embed-perf-zero-state"
          >
            No impressions yet. Embed the snippet on your academy site or a
            partner page to start tracking <span className="font-semibold">live impressions, CTR, and top referrer hosts</span> right here.
          </div>
        ) : (
        <>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-0.5" data-testid="embed-perf-impressions">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 font-bold">
              <Eye className="h-3 w-3" /> Impressions
            </div>
            <div className="text-xl font-extrabold text-slate-900 dark:text-slate-50 tabular-nums">{formatInt(impressions)}</div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400">widget entered viewport</div>
          </div>
          <div className="space-y-0.5" data-testid="embed-perf-ctr">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 font-bold">
              <MousePointerClick className="h-3 w-3" /> CTR
            </div>
            <div className="text-xl font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums">{formatPct(ctr)}</div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400">{formatInt(ctaClicks)} wall visits</div>
          </div>
          <div className="space-y-0.5" data-testid="embed-perf-top-host">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 font-bold">
              <Globe className="h-3 w-3" /> Top referrer
            </div>
            <div className="text-sm font-bold text-slate-900 dark:text-slate-50 truncate" title={topHosts[0]?.host || '—'}>
              {topHosts[0]?.host || '—'}
            </div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center justify-between gap-2">
              <span>{topHosts[0] ? `${formatInt(topHosts[0].count)} views` : 'host unknown'}</span>
              {topHosts[0] && (
                thankedHosts.has(topHosts[0].host) ? (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 dark:text-emerald-300"
                    data-testid="embed-perf-thanked-pill"
                  >
                    <Check className="h-3 w-3" /> Thanked
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setDialogHost({ host: topHosts[0].host, count: topHosts[0].count })}
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600 hover:text-rose-700 dark:text-rose-400 cursor-pointer transition-colors"
                    data-testid="embed-perf-thank-btn"
                  >
                    <Heart className="h-3 w-3" /> Thank
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        {topHosts.length > 1 && (
          <div className="mt-3 pt-3 border-t border-emerald-100 dark:border-emerald-900/40">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1.5">Other referrers</div>
            <div className="flex flex-wrap gap-1.5" data-testid="embed-perf-other-hosts">
              {topHosts.slice(1).map((h) => {
                const thanked = thankedHosts.has(h.host);
                return (
                  <button
                    type="button"
                    key={h.host}
                    onClick={thanked ? undefined : () => setDialogHost({ host: h.host, count: h.count })}
                    disabled={thanked}
                    className={
                      'text-[10px] font-mono border rounded-full px-2 py-0.5 transition-colors inline-flex items-center gap-1 ' +
                      (thanked
                        ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 cursor-default'
                        : 'bg-white/80 dark:bg-slate-900/40 border-emerald-200 dark:border-emerald-900/60 text-slate-700 dark:text-slate-200 hover:border-rose-300 hover:text-rose-700 cursor-pointer')
                    }
                    title={thanked ? `Already thanked (${h.count} views)` : `Click to thank — ${h.count} views`}
                    data-testid={`embed-perf-other-host-${h.host}`}
                  >
                    {thanked && <Check className="h-2.5 w-2.5" />}
                    {h.host} · {formatInt(h.count)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Phase 266h — variant breakdown.  Only show when at least one
            variant has impressions AND we have data on both buckets so
            owners can do a real A/B comparison.  Stays silent for owners
            who've only embedded the default (light) — no judgmental
            "you should try dark too" empty state. */}
        {(() => {
          const vb = Array.isArray(data?.variant_breakdown) ? data.variant_breakdown : [];
          const light = vb.find((v) => v.theme === 'light');
          const dark = vb.find((v) => v.theme === 'dark');
          if (!light || !dark) return null;
          if ((light.impressions || 0) === 0 && (dark.impressions || 0) === 0) return null;
          if ((light.impressions || 0) === 0 || (dark.impressions || 0) === 0) return null;
          const winner = (dark.ctr ?? 0) > (light.ctr ?? 0) ? 'dark' : 'light';
          return (
            <div className="mt-3 pt-3 border-t border-emerald-100 dark:border-emerald-900/40" data-testid="embed-perf-variant-breakdown">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1.5">Theme A/B (last 30d)</div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                {[light, dark].map((v) => (
                  <div
                    key={v.theme}
                    className={
                      'rounded-md px-2.5 py-1.5 border ' +
                      (v.theme === winner
                        ? 'border-emerald-400 dark:border-emerald-700 bg-emerald-50/80 dark:bg-emerald-950/30'
                        : 'border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-950/30')
                    }
                    data-testid={`embed-perf-variant-${v.theme}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold capitalize text-slate-800 dark:text-slate-100">{v.theme}</span>
                      {v.theme === winner && (
                        <span className="text-[9px] font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-300" data-testid={`embed-perf-variant-winner-${v.theme}`}>
                          Best CTR
                        </span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <span className="font-extrabold text-slate-900 dark:text-slate-50 tabular-nums">{formatPct(v.ctr)}</span>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 tabular-nums">{formatInt(v.impressions)} imp · {formatInt(v.cta_clicks)} cta</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        </>
        )}
      </CardContent>

      {/* Phase 266e — thank-you outreach dialog, mounted once and driven
          by dialogHost state.  Receiving onSent flips the host into the
          "Thanked" optimistic state so the button doesn't fire twice. */}
      <ReferrerThankYouDialog
        open={!!dialogHost}
        onOpenChange={(next) => { if (!next) setDialogHost(null); }}
        host={dialogHost?.host || ''}
        viewCount={dialogHost?.count || 0}
        onSent={handleSent}
      />

      {/* Phase 266f — embed-snippet dialog. Reuses the exact same
          EmbedTopMomentsBlock that's mounted inline on /settings, so the
          snippet text and clipboard behaviour stays in single source. */}
      <Dialog open={snippetOpen} onOpenChange={setSnippetOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="embed-snippet-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code2 className="h-4 w-4 text-emerald-600" />
              Embed Top Moments anywhere
            </DialogTitle>
            <DialogDescription>
              Paste this single &lt;script&gt; onto any partner site, school page, or your own academy website. Every new embed feeds straight back into the dashboard above.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <EmbedTopMomentsBlock slug={user?.academy_slug} />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
